/**
 * FeeHistorySection — per-agreement View History + invoice lifecycle (#17 slice).
 *
 * Pick an agreement → see its per-account billing history grouped by month
 * (newest first) in an Accordion. Each month's control shows the agreement-level
 * totals (Σ AuM / Net / VAT / Total) + a compact status summary; the panel shows
 * the per-account rows with the invoice cross-check and the lifecycle actions.
 *
 * Lifecycle (MIS posture — all transitions allowed, escalating warnings, no hard
 * block):
 *   frozen  → Upload invoice (frozen → issued)
 *   issued  → Settle · Amend · Cancel · Download
 *   settled → Download · Unsettle · Amend · Cancel   (louder confirm on the last
 *             three: the fee is marked settled/paid)
 *
 * Friendly labels everywhere (§3j): account = bank · number (Direct) or
 * insurer · policy# (Indirect); never a raw UUID.
 */
import { useMemo, useState } from 'react';
import {
  Accordion, ActionIcon, Badge, Group, Loader, Paper, rem, SegmentedControl, Stack, Table, Text, Tooltip,
} from '@mantine/core';
import { MonthPickerInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import {
  IconAlertTriangle, IconCheck, IconChevronDown, IconDownload, IconPencil, IconReceiptOff, IconReceiptRefund,
} from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { AgreementPicker } from './EntityPicker';
import { UploadInvoiceModal, type InvoiceModalMode, type UploadInvoiceSubmit } from './UploadInvoiceModal';
import { useAlertModal } from './AlertModalProvider';
import { PeriodRangeSelect, resolveWindow } from './periodRange';
import type { DataSpan, PeriodPreset } from './periodRange';
import {
  feeInvoicesApi, type FeeHistoryRow, type FeeInvoiceStatus,
} from '../api/feeInvoices';
import { centsToEur, eurToCents, fmtEurFromCents } from '../api/feeSchedules';
import type { UUID } from '../api/types';

const HISTORY_KEY = (agreementId: string) => ['fee-history', agreementId] as const;

// ── helpers ──────────────────────────────────────────────────────────────

/** "2026-03-01" / "2026-03" → "March 2026". */
function fmtMonth(periodMonth: string): string {
  const ym = periodMonth.slice(0, 7);            // 'YYYY-MM'
  const [y, m] = ym.split('-').map(Number);
  if (!y || !m) return ym;
  const d = new Date(Date.UTC(y, m - 1, 1));
  return d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

/** Effective rate from bps → "0.87%". "—" when unknown. */
function bpsPct(bps: number | null | undefined): string {
  if (bps == null) return '—';
  return `${(bps / 100).toFixed(2)}%`;
}

/** Friendly account label: Direct → bank · number; Indirect → bank · number · insurer · policy#.
 * The bank·account is shown for INDIRECT too, so the underlying account is identifiable rather
 * than only the insurer·policy (Pedro 2026-07-24; mirrors the Compute-Month viewer). */
function accountLabel(r: FeeHistoryRow): string {
  const bankAcct = r.bank_name
    ? (r.account_number ? `${r.bank_name} · ${r.account_number}` : r.bank_name)
    : null;
  if (r.is_direct) return bankAcct || 'Account';
  const insurer = r.insurer || 'Policy';
  const policy = r.policy_number ? `${insurer} · ${r.policy_number}` : insurer;
  return bankAcct ? `${bankAcct} · ${policy}` : policy;
}

const STATUS_META: Record<FeeInvoiceStatus, { color: string; label: string }> = {
  frozen:  { color: 'blue',    label: 'Frozen' },
  issued:  { color: 'laranja', label: 'Issued' },
  settled: { color: 'verde.7', label: 'Settled' },
};

function StatusBadge({ status }: { status: FeeInvoiceStatus }) {
  const m = STATUS_META[status];
  return <Badge color={m.color} variant="light">{m.label}</Badge>;
}

interface MonthGroup {
  ym: string;                  // 'YYYY-MM' sort key
  rows: FeeHistoryRow[];
  sumAum: number;
  sumNet: number;
  sumVat: number;
  sumTotal: number;
  counts: Record<FeeInvoiceStatus, number>;
  mismatchCount: number;       // accounts whose invoice fails the ±€1 cross-check
}

function groupByMonth(rows: FeeHistoryRow[]): MonthGroup[] {
  const map = new Map<string, MonthGroup>();
  for (const r of rows) {
    const ym = r.period_month.slice(0, 7);
    let g = map.get(ym);
    if (!g) {
      g = { ym, rows: [], sumAum: 0, sumNet: 0, sumVat: 0, sumTotal: 0, counts: { frozen: 0, issued: 0, settled: 0 }, mismatchCount: 0 };
      map.set(ym, g);
    }
    g.rows.push(r);
    g.sumAum += r.aum_eur_cents ?? 0;
    g.sumNet += r.net_fee_cents;
    g.sumVat += r.vat_cents;
    g.sumTotal += r.total_fee_cents;
    g.counts[r.status] += 1;
    if (r.net_mismatch || r.total_mismatch) g.mismatchCount += 1;
  }
  // Newest month first.
  return [...map.values()].sort((a, b) => (a.ym < b.ym ? 1 : a.ym > b.ym ? -1 : 0));
}

/** "2 settled · 1 issued · 1 frozen" — only the non-zero buckets. */
function statusSummary(counts: Record<FeeInvoiceStatus, number>): string {
  const parts: string[] = [];
  if (counts.settled) parts.push(`${counts.settled} settled`);
  if (counts.issued) parts.push(`${counts.issued} issued`);
  if (counts.frozen) parts.push(`${counts.frozen} frozen`);
  return parts.join(' · ');
}

interface AgrGroup {
  agreementId: string;
  code: string;
  codeKey: string;
  mnemonic: string;
  rows: FeeHistoryRow[];
  sumAum: number;
  sumNet: number;
  sumVat: number;
  sumTotal: number;
  counts: Record<FeeInvoiceStatus, number>;
  mismatchCount: number;       // accounts whose invoice fails the ±€1 cross-check
}

/** Per-Month view: group the month's rows by agreement, sorted by code (CIIF-nnn). */
function groupByAgreement(rows: FeeHistoryRow[]): AgrGroup[] {
  const map = new Map<string, AgrGroup>();
  for (const r of rows) {
    let g = map.get(r.agreement_id);
    if (!g) {
      g = {
        agreementId: r.agreement_id, code: r.agreement_code || '—', codeKey: r.agreement_code || '',
        mnemonic: r.agreement_mnemonic || '', rows: [],
        sumAum: 0, sumNet: 0, sumVat: 0, sumTotal: 0, counts: { frozen: 0, issued: 0, settled: 0 }, mismatchCount: 0,
      };
      map.set(r.agreement_id, g);
    }
    g.rows.push(r);
    g.sumAum += r.aum_eur_cents ?? 0;
    g.sumNet += r.net_fee_cents;
    g.sumVat += r.vat_cents;
    g.sumTotal += r.total_fee_cents;
    g.counts[r.status] += 1;
    if (r.net_mismatch || r.total_mismatch) g.mismatchCount += 1;
  }
  return [...map.values()].sort((a, b) => (a.codeKey < b.codeKey ? -1 : a.codeKey > b.codeKey ? 1 : 0));
}

// ── component ──────────────────────────────────────────────────────────────

interface ModalState {
  mode: InvoiceModalMode;
  row: FeeHistoryRow;
}

export function FeeHistorySection(
  { fixedAgreementId, readOnly = false }: {
    fixedAgreementId?: UUID;
    /** P45 (Pedro 2026-10-15): RM / Compliance on Fees → History see every row and can
     *  open the invoice PDFs, but settle / amend / unsettle / delete are hidden (the
     *  server refuses them too). The agreement mirror (fixedAgreementId) is always read-only. */
    readOnly?: boolean;
  } = {},
) {
  const rowsReadOnly = readOnly || !!fixedAgreementId;
  const qc = useQueryClient();
  const { confirm, showAlert } = useAlertModal();
  // When fixedAgreementId is set (the Agreement-detail Fees mirror, Inc 8), the
  // view is locked to Per-Agreement on that agreement: the view toggle and the
  // agreement picker are hidden, but the Total card, period filter, month accordion
  // and invoice lifecycle are identical to Admin\Fees (Pedro 2026-06-26).
  const [view, setView] = useState<'agreement' | 'month'>('agreement');
  const [agreementId, setAgreementId] = useState<UUID | null>(fixedAgreementId ?? null);
  const [month, setMonth] = useState('');                  // 'YYYY-MM-01' for the Per-Month view
  const [modal, setModal] = useState<ModalState | null>(null);
  // Period selector (drives the Total card AND filters the Per-Agreement month list).
  // Defaults to the last 3 months (#4, 2026-07-03).
  const [preset, setPreset] = useState<PeriodPreset>('3m');
  const [since, setSince] = useState<Date | null>(null);
  // Fee data span (first/last month WITH fee data) anchors the period window on data,
  // not the calendar (Max / Last-Nm / horizon). Per-agreement span in Per-Agreement
  // view (read-open); firm span in Per-Month (Admin-only). Drives BOTH the Total card
  // (passed to FeePeriodTotal) and the month-list filter below.
  const scopedAgreement = view === 'agreement' ? agreementId : null;
  const needsAgreement = view === 'agreement' && !agreementId;
  const spanQ = useQuery({
    queryKey: ['fee-data-span', view, scopedAgreement ?? ''] as const,
    queryFn: () => feeInvoicesApi.dataSpan(scopedAgreement),
    enabled: !needsAgreement,
  });
  const span: DataSpan = {
    first: spanQ.data?.first_month ? spanQ.data.first_month.slice(0, 7) : null,
    last: spanQ.data?.last_month ? spanQ.data.last_month.slice(0, 7) : null,
  };
  const periodWin = resolveWindow(preset, since, span);

  const historyQ = useQuery({
    queryKey: HISTORY_KEY(agreementId ?? ''),
    queryFn: () => feeInvoicesApi.listHistory(agreementId as UUID),
    enabled: view === 'agreement' && !!agreementId,
  });
  const monthQ = useQuery({
    queryKey: ['fee-history', 'month', month] as const,
    queryFn: () => feeInvoicesApi.listMonthHistory(month.slice(0, 7)),
    enabled: view === 'month' && !!month,
  });
  // Historical (<= Dec-2025) uploaded fee actuals for the Per-Agreement view — folded in
  // as non-expandable 'Uploaded' rows below the computed months (F4, 2026-07-04). Computed
  // (>= Jan-2026) and uploaded (<= Dec-2025) are temporally disjoint at the cut-off, so
  // the list is simply [computed newest-first] then [uploaded newest-first].
  const uploadedQ = useQuery({
    queryKey: ['fee-history', 'uploaded', agreementId ?? ''] as const,
    queryFn: () => feeInvoicesApi.listUploadedHistory(agreementId as UUID),
    enabled: view === 'agreement' && !!agreementId,
  });

  // Any lifecycle write changes the runs/invoices — refetch BOTH views (prefix).
  const invalidate = () => qc.invalidateQueries({ queryKey: ['fee-history'] });

  // ── mutations (each invalidates the history + toasts) ────────────────────
  // Invoice CREATION (frozen → issued) is import-only via the dedicated Import-invoices
  // page (discrete sequential steps, Pedro 2026-07-04) — no per-row upload here; this
  // section handles the post-import lifecycle (amend / settle / unsettle / delete).
  const amendMut = useMutation({
    mutationFn: (vars: { invoiceId: UUID; v: UploadInvoiceSubmit }) =>
      feeInvoicesApi.amend(vars.invoiceId, {
        invoiced_net_cents: eurToCents(vars.v.netEur),
        invoiced_total_cents: eurToCents(vars.v.totalEur),
        file: vars.v.file,
      }),
    onSuccess: () => {
      notifications.show({ color: 'green', message: 'Invoice amended.' });
      setModal(null);
      invalidate();
    },
    onError: (err) =>
      showAlert({ color: 'red', title: 'Could not amend invoice', message: err instanceof Error ? err.message : 'Unknown error' }),
  });

  const settleMut = useMutation({
    mutationFn: (invoiceId: UUID) => feeInvoicesApi.settle(invoiceId),
    onSuccess: () => { notifications.show({ color: 'green', message: 'Invoice settled.' }); invalidate(); },
    onError: (err) => showAlert({ color: 'red', title: 'Could not settle invoice', message: err instanceof Error ? err.message : 'Unknown error' }),
  });

  const unsettleMut = useMutation({
    mutationFn: (invoiceId: UUID) => feeInvoicesApi.unsettle(invoiceId),
    onSuccess: () => { notifications.show({ color: 'green', message: 'Invoice re-opened (Issued).' }); invalidate(); },
    onError: (err) => showAlert({ color: 'red', title: 'Could not unsettle invoice', message: err instanceof Error ? err.message : 'Unknown error' }),
  });

  const cancelMut = useMutation({
    mutationFn: (invoiceId: UUID) => feeInvoicesApi.cancel(invoiceId),
    onSuccess: () => { notifications.show({ color: 'green', message: 'Invoice cancelled.' }); invalidate(); },
    onError: (err) => showAlert({ color: 'red', title: 'Could not cancel invoice', message: err instanceof Error ? err.message : 'Unknown error' }),
  });

  async function downloadPdf(invoiceId: UUID) {
    try {
      await feeInvoicesApi.downloadPdf(invoiceId);
    } catch (err) {
      showAlert({ color: 'red', title: 'Could not download PDF', message: err instanceof Error ? err.message : 'Unknown error' });
    }
  }

  // Confirm dialog that states EXACTLY what the action will do (§3j action
  // transparency). Each call passes its own precise consequence text.
  async function confirmThenRun(opts: {
    title: string; body: string; confirmLabel: string; destructive?: boolean; run: () => void;
  }) {
    const ok = await confirm({
      title: opts.title,
      message: opts.body,
      confirmLabel: opts.confirmLabel,
      cancelLabel: 'No, keep as is',
      destructive: !!opts.destructive,
    });
    if (ok) opts.run();
  }

  // Array.isArray (not just truthy): a not-yet-restarted backend returns the SPA
  // HTML for a brand-new GET route (catch-all fallthrough), which would otherwise
  // be grouped char-by-char into Accordion.Items with empty values and crash.
  // Per-Agreement list is filtered to the selected period window (Pedro 2026-06-25):
  // the on-screen months match the Total card above. A null window ('Since…' with no
  // start month yet) shows everything until the start is chosen.
  const groups = useMemo(
    () => {
      if (!Array.isArray(historyQ.data)) return [];
      const rows = periodWin
        ? historyQ.data.filter((r) => {
            const m = r.period_month.slice(0, 7);
            return m >= periodWin.from && m <= periodWin.to;
          })
        : historyQ.data;
      return groupByMonth(rows);
    },
    [historyQ.data, periodWin?.from, periodWin?.to],
  );
  const monthGroups = useMemo(
    () => (Array.isArray(monthQ.data) ? groupByAgreement(monthQ.data) : []),
    [monthQ.data],
  );
  // Uploaded historical months, filtered to the same period window as the computed months.
  const uploadedInWindow = useMemo(
    () => {
      if (!Array.isArray(uploadedQ.data)) return [];
      return periodWin
        ? uploadedQ.data.filter((m) => {
            const mm = m.period_month.slice(0, 7);
            return mm >= periodWin.from && mm <= periodWin.to;
          })
        : uploadedQ.data;
    },
    [uploadedQ.data, periodWin?.from, periodWin?.to],
  );

  // The per-account rows table — shared by the Per-Agreement (grouped by month) and
  // Per-Month (grouped by agreement) views; identical lifecycle handlers.
  const renderTable = (rows: FeeHistoryRow[]) => {
    // #4 (2026-07-03): in the read-only agreement mirror, drop the Actions column
    // when nothing is downloadable (all rows frozen / no invoice) — no empty column.
    const showActions = !rowsReadOnly || rows.some((r) => r.status !== 'frozen');
    return (
    <Table.ScrollContainer minWidth={900}>
      <Table withTableBorder striped verticalSpacing="xs">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Account</Table.Th>
            <Table.Th ta="right">AuM</Table.Th>
            <Table.Th ta="right">Net</Table.Th>
            <Table.Th ta="right">VAT</Table.Th>
            <Table.Th ta="right">Total</Table.Th>
            <Table.Th ta="right">Rate</Table.Th>
            <Table.Th>Cross-check</Table.Th>
            <Table.Th>Status</Table.Th>
            {showActions && <Table.Th ta="right">Actions</Table.Th>}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((r) => (
            <HistoryRow
              key={r.run_id}
              r={r}
              onAmend={() =>
                r.status === 'settled'
                  ? confirmThenRun({
                      title: 'Amend a settled invoice?',
                      body: 'This invoice is marked SETTLED (paid). Amending changes its recorded Net/Total and/or replaces the PDF; it stays settled, but the amounts the cross-check compares against the computed fee will change. Only correct a settled invoice this way, and reconcile it with the accounts.',
                      confirmLabel: 'Amend the settled invoice',
                      destructive: true,
                      run: () => setModal({ mode: 'amend', row: r }),
                    })
                  : setModal({ mode: 'amend', row: r })}
              onSettle={() =>
                r.invoice_id && confirmThenRun({
                  title: 'Mark this invoice settled?',
                  body: 'This marks the invoice SETTLED (paid): it will show as Settled in the history and count as reconciled. You can Unsettle it later if needed.',
                  confirmLabel: 'Mark settled',
                  run: () => settleMut.mutate(r.invoice_id as UUID),
                })}
              onUnsettle={() =>
                r.invoice_id && confirmThenRun({
                  title: 'Re-open this settled invoice?',
                  body: 'This invoice is SETTLED (paid). Unsettling moves it back to ISSUED — it will no longer count as settled/paid until you settle it again. The PDF and amounts are kept.',
                  confirmLabel: 'Re-open (unsettle)',
                  destructive: true,
                  run: () => unsettleMut.mutate(r.invoice_id as UUID),
                })}
              onCancel={() =>
                r.invoice_id && confirmThenRun({
                  title: r.status === 'settled' ? 'Delete a settled invoice?' : 'Delete this invoice?',
                  body: r.status === 'settled'
                    ? 'This invoice is SETTLED (paid). Deleting removes it from the history (it stays in the audit trail) and returns this account-month to FROZEN — it will no longer show as invoiced or settled, and a new invoice can be uploaded. Only do this to fix a mistake, and reconcile with the accounts.'
                    : 'Deleting removes this invoice from the history (it stays in the audit trail) and returns this account-month to FROZEN, so a new invoice can be uploaded.',
                  confirmLabel: 'Delete the invoice',
                  destructive: true,
                  run: () => cancelMut.mutate(r.invoice_id as UUID),
                })}
              onDownload={() => r.invoice_id && downloadPdf(r.invoice_id)}
              readOnly={rowsReadOnly}
              showActions={showActions}
            />
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
    );
  };

  return (
    <Stack>
      {!fixedAgreementId && (
        <SegmentedControl
          value={view}
          onChange={(v) => setView(v as 'agreement' | 'month')}
          data={[{ label: 'Per agreement', value: 'agreement' }, { label: 'Per month', value: 'month' }]}
          maw={320}
        />
      )}

      <FeePeriodTotal
        view={view}
        agreementId={agreementId}
        preset={preset}
        onPreset={setPreset}
        since={since}
        onSince={setSince}
        win={periodWin}
      />

      {view === 'agreement' && (
      <>
      {!fixedAgreementId && (
        <AgreementPicker
          label="Agreement"
          placeholder="Pick an agreement to see its fee history"
          value={agreementId}
          onChange={setAgreementId}
        />
      )}

      {!agreementId && (
        <Text size="sm" c="dimmed">Pick an agreement above to view its computed fees.</Text>
      )}

      {agreementId && historyQ.isLoading && (
        <Group gap="xs"><Loader size="sm" /><Text size="sm" c="dimmed">Loading history…</Text></Group>
      )}

      {agreementId && !historyQ.isLoading && !uploadedQ.isLoading
        && groups.length === 0 && uploadedInWindow.length === 0 && (
        <Paper withBorder p="lg" radius="md">
          <Text size="sm" c="dimmed">
            {(Array.isArray(historyQ.data) && historyQ.data.length > 0)
              || (Array.isArray(uploadedQ.data) && uploadedQ.data.length > 0)
              ? 'No fees for this agreement in the selected period — widen the period above.'
              : 'No fees yet for this agreement.'}
          </Text>
        </Paper>
      )}

      {groups.length > 0 && (
        <Accordion variant="separated" multiple={false}>
          {groups.map((g) => (
            <Accordion.Item key={g.ym} value={g.ym}>
              <Accordion.Control>
                <Group justify="space-between" wrap="nowrap" pr="md">
                  <Group gap="sm" wrap="nowrap">
                    <Text fw={600}>{fmtMonth(g.ym)}</Text>
                    <Badge color="blue" variant="light">Calculated</Badge>
                    <CrossCheckWarn count={g.mismatchCount} />
                  </Group>
                  <Group gap="lg" wrap="nowrap">
                    <MonthTotal label="Σ AuM" cents={g.sumAum} w={FEE_COLW.aum} />
                    <MonthTotal label="Σ Net" cents={g.sumNet} w={FEE_COLW.money} />
                    <MonthTotal label="Σ VAT" cents={g.sumVat} w={FEE_COLW.money} />
                    <MonthTotal label="Σ Total" cents={g.sumTotal} strong w={FEE_COLW.money} />
                    <Text size="xs" c="dimmed" style={{ width: FEE_COLW.status, textAlign: 'right' }}>{statusSummary(g.counts)}</Text>
                  </Group>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>{renderTable(g.rows)}</Accordion.Panel>
            </Accordion.Item>
          ))}
        </Accordion>
      )}

      {/* Historical uploaded fee actuals (<= Dec-2025): agreement-grain, non-expandable
          (no per-account detail exists) — clicking does nothing (Pedro 2026-07-04). Same
          Accordion structure as the computed months (variant + invisible chevron + fixed
          column widths) so the columns line up; no Panel so nothing opens. They sit below
          the computed months since they are always older than the cut-off. */}
      {uploadedInWindow.length > 0 && (
        <Accordion
          variant="separated"
          multiple={false}
          chevron={<IconChevronDown size={16} style={{ opacity: 0 }} />}
          styles={{ control: { cursor: 'default' } }}
        >
          {uploadedInWindow.map((m) => (
            <Accordion.Item key={m.period_month} value={m.period_month}>
              <Accordion.Control>
                <Group justify="space-between" wrap="nowrap" pr="md">
                  <Group gap="sm" wrap="nowrap">
                    <Text fw={600}>{fmtMonth(m.period_month)}</Text>
                    <Badge color="gray" variant="light">Uploaded</Badge>
                  </Group>
                  <Group gap="lg" wrap="nowrap">
                    <MonthTotal label="Σ AuM" cents={m.aum_eur_cents ?? 0} w={FEE_COLW.aum} />
                    <MonthTotal label="Σ Net" cents={m.fee_ex_vat_cents} strong w={FEE_COLW.money} />
                    <MonthDash label="Σ VAT" w={FEE_COLW.money} />
                    <MonthDash label="Σ Total" w={FEE_COLW.money} />
                    <Text size="xs" c="dimmed" style={{ width: FEE_COLW.status, textAlign: 'right' }}>actual</Text>
                  </Group>
                </Group>
              </Accordion.Control>
            </Accordion.Item>
          ))}
        </Accordion>
      )}
      </>
      )}

      {view === 'month' && (
      <>
      <MonthPickerInput
        label="Billing month"
        placeholder="Pick a month to see every agreement's fees"
        value={month ? new Date(`${month}T00:00:00`) : null}
        onChange={(d) => setMonth(d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01` : '')}
        maw={240}
      />
      {!month && (
        <Text size="sm" c="dimmed">Pick a month above to view every agreement's fees for it.</Text>
      )}
      {month && monthQ.isLoading && (
        <Group gap="xs"><Loader size="sm" /><Text size="sm" c="dimmed">Loading month…</Text></Group>
      )}
      {month && !monthQ.isLoading && monthGroups.length === 0 && (
        <Paper withBorder p="lg" radius="md">
          <Text size="sm" c="dimmed">No computed fees for this month.</Text>
        </Paper>
      )}
      {monthGroups.length > 0 && (
        <Accordion variant="separated" multiple>
          {monthGroups.map((g) => (
            <Accordion.Item key={g.agreementId} value={g.agreementId}>
              <Accordion.Control>
                <Group justify="space-between" wrap="nowrap" pr="md">
                  <Group gap="xl" wrap="nowrap">
                    <Stack gap={0}><Text size="xs" c="dimmed">Code</Text><Text fw={600}>{g.code}</Text></Stack>
                    <Stack gap={0}><Text size="xs" c="dimmed">Mnemonic</Text><Text>{g.mnemonic || '—'}</Text></Stack>
                    <CrossCheckWarn count={g.mismatchCount} />
                  </Group>
                  <Group gap="lg" wrap="nowrap">
                    <MonthTotal label="Σ AuM" cents={g.sumAum} />
                    <MonthTotal label="Σ Net" cents={g.sumNet} />
                    <MonthTotal label="Σ Total" cents={g.sumTotal} strong />
                    <Text size="xs" c="dimmed">{statusSummary(g.counts)}</Text>
                  </Group>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>{renderTable(g.rows)}</Accordion.Panel>
            </Accordion.Item>
          ))}
        </Accordion>
      )}
      </>
      )}

      {modal && (
        <UploadInvoiceModal
          opened
          mode={modal.mode}
          onClose={() => setModal(null)}
          accountLabel={accountLabel(modal.row)}
          monthLabel={fmtMonth(modal.row.period_month)}
          initialNetEur={modal.mode === 'amend' && modal.row.invoiced_net_cents != null
            ? centsToEur(modal.row.invoiced_net_cents) : undefined}
          initialTotalEur={modal.mode === 'amend' && modal.row.invoiced_total_cents != null
            ? centsToEur(modal.row.invoiced_total_cents) : undefined}
          submitting={amendMut.isPending}
          onSubmit={(v) => {
            if (modal.row.invoice_id) {
              amendMut.mutate({ invoiceId: modal.row.invoice_id, v });
            }
          }}
        />
      )}
    </Stack>
  );
}

// Fixed column widths so the computed and uploaded month rows line up vertically
// despite different value widths (Pedro 2026-07-04).
const FEE_COLW = { aum: rem(128), money: rem(92), status: rem(72) } as const;

function MonthTotal({ label, cents, strong, w }: { label: string; cents: number; strong?: boolean; w?: string | number }) {
  return (
    <Stack gap={0} align="flex-end" w={w}>
      <Text size="xs" c="dimmed">{label}</Text>
      <Text size="sm" fw={strong ? 700 : 500}>€{fmtEurFromCents(cents)}</Text>
    </Stack>
  );
}

/** A dashed column (label + "—"), so an uploaded historical month lines up under the
 *  computed months' Σ VAT / Σ Total columns even though those values are unknown. */
function MonthDash({ label, w }: { label: string; w?: string | number }) {
  return (
    <Stack gap={0} align="flex-end" w={w}>
      <Text size="xs" c="dimmed">{label}</Text>
      <Text size="sm" c="dimmed">—</Text>
    </Stack>
  );
}

/** Roll-up attention flag on a collapsed group control: drawn only when one or more
 *  of the (hidden) accounts has a cross-check that differs by more than €1, so the
 *  operator knows to expand and review without first opening every group (§3j). */
function CrossCheckWarn({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <Tooltip
      withArrow
      label={`${count} account${count > 1 ? 's' : ''} where the invoiced amount differs from the computed fee by more than €1 — expand to review`}
    >
      <Badge color="bordeaux" variant="filled" leftSection={<IconAlertTriangle size={12} />} style={{ cursor: 'help' }}>
        {count} cross-check
      </Badge>
    </Tooltip>
  );
}

// ── Period Total (top-of-page) ───────────────────────────────────────────────
// Period presets + window resolution now come from the shared unified mechanism
// (components/periodRange). The old calendar "last completed month" heuristic
// (#4, 2026-07-03) is superseded by anchoring on the FEE data span (first/last
// month with fee data) — which is data-driven, adds Max/since-inception, and can't
// fall short at month-start. See fee_data_span + GET /agreement-fee-invoices/data-span.

/** Top-of-page Σ of frozen fees over a chosen window. Scope follows the current
 *  View-History view (§3j): Per-Agreement → the selected agreement; Per-Month →
 *  firm-wide. Shows Σ Net (ex VAT) and Σ Total (incl VAT). Basis = computed/frozen
 *  fees, not invoiced amounts (Pedro 2026-06-25). */
function FeePeriodTotal({
  view, agreementId, preset, onPreset, since, onSince,
  win,
}: {
  view: 'agreement' | 'month';
  agreementId: UUID | null;
  preset: PeriodPreset;
  onPreset: (p: PeriodPreset) => void;
  since: Date | null;
  onSince: (d: Date | null) => void;
  win: { from: string; to: string } | null;   // resolved upstream (shared with the month-list filter)
}) {
  const scopedAgreement = view === 'agreement' ? agreementId : null;
  const needsAgreement = view === 'agreement' && !agreementId;

  const q = useQuery({
    queryKey: ['fee-period-total', view, scopedAgreement ?? '', win?.from ?? '', win?.to ?? ''] as const,
    queryFn: () => feeInvoicesApi.periodTotal(win!.from, win!.to, scopedAgreement),
    enabled: !!win && !needsAgreement,
  });

  const scopeLabel = view === 'month' ? 'Firm-wide' : 'Selected agreement';
  const windowLabel = win ? `${fmtMonth(win.from)} – ${fmtMonth(win.to)}` : '—';

  return (
    <Paper withBorder p="md" radius="md">
      <Group justify="space-between" wrap="wrap" gap="md">
        <Group gap="md" wrap="wrap">
          <Text fw={600}>Total</Text>
          <PeriodRangeSelect preset={preset} onPreset={onPreset} since={since} onSince={onSince} />
        </Group>
        <Group gap="xl" wrap="nowrap">
          {q.isLoading && <Loader size="sm" />}
          {!needsAgreement && q.data && (
            <Stack gap={0} align="flex-end">
              {/* Ex-VAT only (Pedro 2026-07-04): the sum combines computed + uploaded
                  historical net; historical carried no VAT, so no incl-VAT figure. */}
              <Text size="xs" c="dimmed">Σ Net (ex VAT)</Text>
              <Text size="md" fw={700}>€{fmtEurFromCents(q.data.sum_net_cents)}</Text>
            </Stack>
          )}
        </Group>
      </Group>
      <Text size="xs" c="dimmed" mt={6}>
        {needsAgreement
          ? 'Pick an agreement below to see its period total.'
          : preset === 'since' && !since
            ? 'Pick a start month to total from.'
            : q.data
              ? `${scopeLabel} · ${windowLabel} · ${q.data.months} month${q.data.months === 1 ? '' : 's'}, ${q.data.runs} fee row${q.data.runs === 1 ? '' : 's'}`
              : `${scopeLabel} · ${windowLabel}`}
      </Text>
    </Paper>
  );
}

function CrossCheck({ r }: { r: FeeHistoryRow }) {
  if (r.status === 'frozen' || r.invoice_id == null) {
    return <Text size="xs" c="dimmed">— no invoice</Text>;
  }
  const mismatch = !!r.net_mismatch || !!r.total_mismatch;
  if (!mismatch) {
    return <Text size="xs" c="dimmed">matches (±€1)</Text>;
  }
  // Largest absolute diff drives the headline number.
  const netDiff = Math.abs(r.net_diff_cents ?? 0);
  const totalDiff = Math.abs(r.total_diff_cents ?? 0);
  const worst = Math.max(netDiff, totalDiff);
  return (
    <Tooltip
      withArrow
      label={
        `Invoiced Net €${fmtEurFromCents(r.invoiced_net_cents ?? 0)} vs computed €${fmtEurFromCents(r.net_fee_cents)} · `
        + `Invoiced Total €${fmtEurFromCents(r.invoiced_total_cents ?? 0)} vs computed €${fmtEurFromCents(r.total_fee_cents)}`
      }
    >
      <Badge color="bordeaux" variant="light" style={{ cursor: 'help' }}>
        ⚠ differs by €{fmtEurFromCents(worst)}
      </Badge>
    </Tooltip>
  );
}

function HistoryRow({
  r, onAmend, onSettle, onUnsettle, onCancel, onDownload, readOnly = false, showActions = true,
}: {
  r: FeeHistoryRow;
  onAmend: () => void;
  onSettle: () => void;
  onUnsettle: () => void;
  onCancel: () => void;
  onDownload: () => void;
  readOnly?: boolean;
  showActions?: boolean;
}) {
  return (
    <Table.Tr>
      <Table.Td>
        <Group gap="xs" wrap="nowrap">
          <Text size="sm">{accountLabel(r)}</Text>
          <Badge size="xs" variant="light" color={r.is_direct ? 'blue' : 'grape'}>
            {r.is_direct ? 'Direct' : 'Indirect'}
          </Badge>
        </Group>
      </Table.Td>
      <Table.Td ta="right"><Text size="sm">{r.aum_eur_cents != null ? `€${fmtEurFromCents(r.aum_eur_cents)}` : '—'}</Text></Table.Td>
      <Table.Td ta="right"><Text size="sm">€{fmtEurFromCents(r.net_fee_cents)}</Text></Table.Td>
      <Table.Td ta="right"><Text size="sm" c="dimmed">€{fmtEurFromCents(r.vat_cents)}</Text></Table.Td>
      <Table.Td ta="right"><Text size="sm" fw={600}>€{fmtEurFromCents(r.total_fee_cents)}</Text></Table.Td>
      <Table.Td ta="right"><Text size="sm">{bpsPct(r.effective_rate_bps)}</Text></Table.Td>
      <Table.Td><CrossCheck r={r} /></Table.Td>
      <Table.Td><StatusBadge status={r.status} /></Table.Td>
      {showActions && (
      <Table.Td>
        <Group gap={4} wrap="nowrap" justify="flex-end">
          {/* #67 (2026-06-28): the Agreement-scoped mirror (fixedAgreementId) is
              READ-ONLY — Download only; all fee actions are Admin-only under Admin\Fees. */}
          {readOnly ? (
            (r.status === 'issued' || r.status === 'settled') && (
              <Tooltip label="Download invoice" withArrow>
                <ActionIcon variant="subtle" color="gray" onClick={onDownload} aria-label="Download invoice">
                  <IconDownload size={16} />
                </ActionIcon>
              </Tooltip>
            )
          ) : (
            <>
          {/* Frozen rows carry no per-row action: an invoice is attached only via the
              Import-invoices page (frozen -> issued happens there), not inline (Pedro 2026-07-04). */}
          {r.status === 'issued' && (
            <>
              <Tooltip label="Settle" withArrow>
                <ActionIcon variant="subtle" color="verde" onClick={onSettle} aria-label="Settle">
                  <IconCheck size={16} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Amend" withArrow>
                <ActionIcon variant="subtle" color="azul" onClick={onAmend} aria-label="Amend">
                  <IconPencil size={16} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Download invoice" withArrow>
                <ActionIcon variant="subtle" color="gray" onClick={onDownload} aria-label="Download invoice">
                  <IconDownload size={16} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Delete invoice" withArrow>
                <ActionIcon variant="subtle" color="bordeaux" onClick={onCancel} aria-label="Delete invoice">
                  <IconReceiptOff size={16} />
                </ActionIcon>
              </Tooltip>
            </>
          )}

          {r.status === 'settled' && (
            <>
              <Tooltip label="Download invoice" withArrow>
                <ActionIcon variant="subtle" color="gray" onClick={onDownload} aria-label="Download invoice">
                  <IconDownload size={16} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Unsettle (re-open)" withArrow>
                <ActionIcon variant="subtle" color="laranja" onClick={onUnsettle} aria-label="Unsettle">
                  <IconReceiptRefund size={16} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Amend" withArrow>
                <ActionIcon variant="subtle" color="azul" onClick={onAmend} aria-label="Amend">
                  <IconPencil size={16} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Delete invoice" withArrow>
                <ActionIcon variant="subtle" color="bordeaux" onClick={onCancel} aria-label="Delete invoice">
                  <IconReceiptOff size={16} />
                </ActionIcon>
              </Tooltip>
            </>
          )}
            </>
          )}
        </Group>
      </Table.Td>
      )}
    </Table.Tr>
  );
}
